import { Component } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import {
  ReceiptComponent,
  ReceiptDisplay,
} from '../../components/receipt/receipt.component';
import { MatDialog } from '@angular/material/dialog';
import { FileProcessorService } from '@app/shared/services/wasm/file-processor/file-processor.service';
import { NotificationService } from '@app/shared/services/notification/notification.service';
import { NotificationhubService } from '@app/shared/services/notificationhub/notificationhub.service';
import {
  IFilters,
  PermanentTablePaginatedComponent,
} from '../../components/permanent-table-paginated/permanent-table-paginated.component';

import {
  AccountReturnClient,
  AccountReturnDto,
  FileParameter,
  IAccountReturnDto,
} from '@tech/api-clients/receipts';
import { PrettyConsole } from '@app/shared/utils';
import { DataSource } from '@angular/cdk/collections';
import { PermanentTableComponent } from '../../components/permanent-table/permanent-table.component';

@Component({
  selector: 'app-receipt-account-return',
  standalone: true,
  imports: [ReceiptComponent, TranslateModule],
  templateUrl: './receipt-account-return.component.html',
  styleUrl: './receipt-account-return.component.scss',
})
export class ReceiptAccountReturnComponent extends ReceiptComponent {
  override display_columns = Object.keys(AccountReturnDto).map((c) =>
    c.toUpperCase()
  );
  override staged_receipts: ReceiptDisplay[] = [];
  override sse_name = 'AccountReturn';
  override available_filters: IFilters[] = [];
  override upload_field_text = 'Account Returns';
  override validation_schema: {}; // This is an xml file
  override wasm_split_pattern: string = ''; // XML file

  constructor(
    dialog: MatDialog,
    fileProcessor: FileProcessorService,
    notify: NotificationService,
    hub: NotificationhubService,
    private child_client: AccountReturnClient
  ) {
    super(dialog, fileProcessor, notify, hub);
    this.sse_name = 'AccountReturn';
    this.validation_schema = {};
    this.upload_handler = this.upload_files_client.bind(this);
    this.lookupPermanent = this.look_today.bind(this);
    this.callme = this.lookup_all.bind(this);
    this.get_paginated_data = this.get_paginated_data.bind(this);
    this.child_client.getFilterStructure(undefined).subscribe({
      next: (x) => {
        var parsed = JSON.parse(x);
        this.available_filters = parsed;
      },
      error: (e) => {
        PrettyConsole.ERROR(
          `Something failed while getting filter structure ${e}`
        );
      },
    });
  }

  upload_files_client = (files: FileParameter[]) => {
    return this.child_client.uploadAccountReturnFiles(files);
  };

  look_today = () => {
    const loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    const ds = new PermanentAccountReturns();
    this.child_client.getAccountReturnsUploadedToday(undefined).subscribe({
      next: (returns) => {
        ds.setData(returns);
        loading$.next(false);
      },
      error: (err) => {
        PrettyConsole.ERROR(
          `getAccountReturnsUploadedToday got an error on subscribe ${err}`
        );
        loading$.next(false);
      },
    });
    this.dialog.open(PermanentTableComponent<PermanentAccountReturns>, {
      width: '85vw',
      height: '80vh',
      data: {
        title: 'Receipts of last remittance',
        receipts: ds,
        display: Object.keys(AccountReturnDto.prototype.toJSON()),
        loading$,
        callback: this.lookup_all,
      },
    });
  };

  lookup_all = () => {
    this.dialog.closeAll();
    if (this.available_filters.length === 0) {
      this.child_client.getFilterStructure(undefined).subscribe({
        next: (x) => {
          var parsed = JSON.parse(x);
          this.available_filters = parsed;
        },
        error: (e) => {
          PrettyConsole.ERROR(
            `Something failed while getting filter structure ${e}`
          );
        },
      });
    }

    const data_source = new PermanentAccountReturns();
    this.dialog.open(PermanentTablePaginatedComponent<IAccountReturnDto>, {
      width: '100vw',
      maxWidth: '98vw',
      height: '95vh',
      data: {
        receipts: data_source,
        display: Object.keys(AccountReturnDto.prototype.toJSON()).filter(
          (d) => d !== 'id'
        ),
        callback: this.get_paginated_data,
        filters: this.available_filters,
      },
    });
  };

  override get_paginated_data = (
    page: number,
    size: number,
    filters: IFilters[]
  ) => {
    let data = new BehaviorSubject<AccountReturnDto[]>([]);
    let count = new BehaviorSubject<number>(0);

    let filter_values: Record<string, any> = {};
    filters.forEach((f) => {
      filter_values[f.id] = f.value === null ? undefined : f.value;
    });

    this.child_client
      .getAllAccountReturnsQuery(page, size, JSON.stringify(filter_values))
      .subscribe({
        error: (_) => {
          PrettyConsole.ERROR(
            `Error while getting the AccountReturn receipts. page: ${page}, size: ${size}`
          );
        },
        next: (d) => {
          if (d.count) count.next(d.count);
		  if (d.receipts) data.next(d.receipts);
		  console.log(d);
        },
      });
    return { count: count.asObservable(), data: data.asObservable() };
  };
}

export class PermanentAccountReturns extends DataSource<IAccountReturnDto> {
  private data = new BehaviorSubject<IAccountReturnDto[]>([]);

  connect(): Observable<IAccountReturnDto[]> {
    return this.data.asObservable();
  }

  addRow(row: IAccountReturnDto) {
    const current = this.data.getValue();
    this.data.next([...current, row]);
  }

  setData(rows: AccountReturnDto[]) {
    const data = rows.map((r) => r.toJSON());
    this.data.next(data);
  }

  disconnect(): void {}
}
